import { STATE_LABELS, type SessionState } from "@switchboard/shared";

export { STATE_LABELS };

export const STATE_TONE: Record<SessionState, "ok" | "warn" | "bad" | "accent" | "muted"> = {
  created: "muted",
  queued: "muted",
  idle: "muted",
  working: "ok",
  waiting: "warn",
  done: "accent",
  rate_limited: "warn",
  stopped: "muted",
  errored: "bad",
};

export function duration(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h ${m % 60}m`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
}

export function ago(iso: string | Date | null | undefined, now = Date.now()): string {
  if (!iso) return "never";
  const t = typeof iso === "string" ? Date.parse(iso) : iso.getTime();
  const s = Math.round((now - t) / 1000);
  if (s < 45) return "just now";
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86_400) return `${Math.round(s / 3600)} h ago`;
  if (s < 2 * 86_400) return "yesterday";
  return `${Math.round(s / 86_400)} days ago`;
}

export function clock(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return sameDay ? time : `${d.toLocaleDateString([], { month: "short", day: "numeric" })} ${time}`;
}

export function fullTime(iso: string): string {
  return new Date(iso).toLocaleString();
}

export function shortId(id: string | null | undefined): string {
  return id ? `${id.slice(0, 4)}…${id.slice(-4)}` : "—";
}

export function basename(p: string): string {
  return p.split(/[\\/]/).filter(Boolean).at(-1) ?? p;
}

/** Path relative to a project's root, for display. */
export function relPath(p: string, root: string): string {
  return p.startsWith(root) ? p.slice(root.length).replace(/^[\\/]/, "").replaceAll("\\", "/") : p;
}

export function plural(n: number, word: string, many = `${word}s`): string {
  return `${n} ${n === 1 ? word : many}`;
}

/** "discord:1234" → { surface: "Discord", id: "1234" }. */
export function actorLabel(actor: string | null | undefined): string {
  if (!actor) return "—";
  if (actor.startsWith("discord:")) return `Discord user ${actor.slice(8)}`;
  if (actor === "web") return "Dashboard";
  return actor[0]!.toUpperCase() + actor.slice(1);
}

export const MODEL_SUGGESTIONS = ["fable", "opus", "sonnet", "haiku", "opusplan"];

/** Aliases follow Claude Code's latest version of each model; pinned IDs stay put. */
export const MODEL_CHOICES: { group: string; models: { value: string; label: string }[] }[] = [
  {
    group: "Latest",
    models: [
      { value: "fable", label: "Fable" },
      { value: "opus", label: "Opus" },
      { value: "sonnet", label: "Sonnet" },
      { value: "haiku", label: "Haiku" },
      { value: "opusplan", label: "Opus plan, Sonnet build" },
    ],
  },
  {
    group: "Pinned",
    models: [
      { value: "claude-fable-5-1", label: "Fable 5.1" },
      { value: "claude-opus-5-5", label: "Opus 5.5" },
      { value: "claude-sonnet-5-5", label: "Sonnet 5.5" },
      { value: "claude-haiku-5-5", label: "Haiku 5.5" },
    ],
  },
];

export const EFFORT_HELP: Record<string, string> = {
  low: "fastest, least thinking",
  medium: "balanced",
  high: "more thinking",
  xhigh: "extra thinking",
  max: "most thinking, slowest",
};
export const PERMISSION_MODES = ["default", "acceptEdits", "plan", "dontAsk", "auto", "bypassPermissions"] as const;
export const PERMISSION_MODE_HELP: Record<string, string> = {
  default: "Ask before edits and commands",
  acceptEdits: "Edit files freely; ask before commands",
  plan: "Read-only planning; no edits",
  dontAsk: "Deny anything that would need asking",
  auto: "Let Claude decide what needs asking",
  bypassPermissions: "Never ask (dangerous)",
};
